/**
 * iOS Universal Links — dash data layer (feature #178).
 *
 * Thin typed wrapper around `apiClient` for the
 * `/api/v1/ios-universal-links` surface. Mirrors the backend contract
 * in `apps/api/src/routes/ios-universal-links.ts`:
 *
 *   POST   /                       { domain, team_id, bundle_id, label,
 *                                    paths? }
 *                                  → { app: View }
 *   GET    /                       → { apps: View[] }  (?domain filters)
 *   GET    /:id                    → { app: View }
 *   PATCH  /:id                    { label?, paths?, status? }
 *                                  → { app: View }
 *   DELETE /:id                    → { deleted | archived } payload.
 *
 *   GET    /preview?domain=<d>     → { domain, aasa, app_count }, used to
 *                                    render the live AASA JSON in the
 *                                    operator UI without touching counters.
 *
 * Pure helpers (validators, draft schema, AASA rendering) live here too so
 * the unit tests can drive them without pulling in a DOM. Validators mirror
 * the server regex; tests assert they agree.
 */

import { apiClient, ApiError } from "@/api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type IosUniversalLinkStatus = "active" | "paused" | "archived";

export const STATUS_LABELS: Record<IosUniversalLinkStatus, string> = {
	active: "Active",
	paused: "Paused",
	archived: "Archived",
};

export const STATUS_ORDER: readonly IosUniversalLinkStatus[] = [
	"active",
	"paused",
	"archived",
];

export interface IosUniversalLinkAppView {
	id: string;
	tenant_id: string;
	domain: string;
	team_id: string;
	bundle_id: string;
	label: string;
	paths: string[];
	status: IosUniversalLinkStatus;
	aasa_serve_count: number;
	last_served_at: number | null;
	created_at: number;
	updated_at: number;
}

export interface AASADocument {
	applinks: {
		apps: string[];
		details: Array<{
			appIDs: string[];
			components: Array<{ "/": string }>;
		}>;
	};
}

// ---------------------------------------------------------------------------
// Validators — mirror server regex. Do not diverge.
// ---------------------------------------------------------------------------

const TEAM_ID_RE = /^[A-Z0-9]{10}$/;
const BUNDLE_ID_RE = /^[a-z][a-z0-9-]{0,62}(?:\.[a-z][a-z0-9-]{0,62}){1,7}$/;
const DOMAIN_RE = /^(?!-)(?:[a-z0-9-]{1,63}(?<!-)\.)+[a-z]{2,63}$/;
export const MAX_PATHS = 32;
export const MAX_APPS_PER_DOMAIN = 8;

export function normalizeDomain(raw: string): string | null {
	if (typeof raw !== "string") return null;
	let v = raw.trim().toLowerCase();
	if (v.length === 0 || v.length > 253) return null;
	if (v.startsWith("http://")) v = v.slice(7);
	else if (v.startsWith("https://")) v = v.slice(8);
	const slash = v.indexOf("/");
	if (slash >= 0) v = v.slice(0, slash);
	const colon = v.indexOf(":");
	if (colon >= 0) v = v.slice(0, colon);
	if (!DOMAIN_RE.test(v)) return null;
	return v;
}

export function normalizeTeamId(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const v = raw.trim().toUpperCase();
	return TEAM_ID_RE.test(v) ? v : null;
}

export function normalizeBundleId(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const v = raw.trim().toLowerCase();
	if (v.length === 0 || v.length > 255) return null;
	return BUNDLE_ID_RE.test(v) ? v : null;
}

export function normalizePath(raw: string): string | null {
	if (typeof raw !== "string") return null;
	const v = raw.trim();
	if (v.length === 0 || v.length > 256) return null;
	if (v === "*") return "*";
	if (!v.startsWith("/")) return null;
	for (let i = 0; i < v.length; i++) {
		const c = v.charCodeAt(i);
		if (c < 0x21 || c === 0x22 || c === 0x5c) return null;
	}
	return v;
}

export function parsePathList(input: string): {
	ok: true;
	paths: string[];
} | { ok: false; bad: string[] } {
	const parts = input
		.split(/[\s,]+/)
		.map((s) => s.trim())
		.filter((s) => s.length > 0);
	const paths: string[] = [];
	const bad: string[] = [];
	for (const raw of parts) {
		const norm = normalizePath(raw);
		if (!norm) {
			bad.push(raw);
			continue;
		}
		if (!paths.includes(norm)) paths.push(norm);
	}
	if (bad.length > 0) return { ok: false, bad };
	return { ok: true, paths };
}

// ---------------------------------------------------------------------------
// Draft + schema
// ---------------------------------------------------------------------------

export interface AppDraft {
	domain: string;
	teamId: string;
	bundleId: string;
	label: string;
	pathsText: string;
}

export const EMPTY_DRAFT: AppDraft = {
	domain: "",
	teamId: "",
	bundleId: "",
	label: "",
	pathsText: "*",
};

export interface ValidatedAppDraft {
	domain: string;
	teamId: string;
	bundleId: string;
	label: string;
	paths: string[];
}

export interface ParseOk<T> {
	success: true;
	data: T;
}
export interface ParseErr {
	success: false;
	errors: Record<string, string>;
}
export type ParseResult<T> = ParseOk<T> | ParseErr;

export const appDraftSchema = {
	safeParse(input: AppDraft): ParseResult<ValidatedAppDraft> {
		const errors: Record<string, string> = {};

		const domain = normalizeDomain(input.domain ?? "");
		if (!domain) {
			errors.domain =
				"Enter the tracking domain (e.g. go.brand.example), no scheme or path.";
		}

		const teamId = normalizeTeamId(input.teamId ?? "");
		if (!teamId) {
			errors.teamId = "Apple Team IDs are exactly 10 uppercase alphanumerics.";
		}

		const bundleId = normalizeBundleId(input.bundleId ?? "");
		if (!bundleId) {
			errors.bundleId = "Use reverse-DNS (e.g. com.brand.app), lowercase.";
		}

		const label = (input.label ?? "").trim();
		if (label.length === 0) {
			errors.label = "Label is required.";
		} else if (label.length > 120) {
			errors.label = "Label must be 120 characters or fewer.";
		}

		const parsed = parsePathList(input.pathsText ?? "");
		let paths: string[] = ["*"];
		if (!parsed.ok) {
			errors.pathsText = `Invalid path(s): ${parsed.bad.join(", ")}`;
		} else if (parsed.paths.length > MAX_PATHS) {
			errors.pathsText = `Too many paths (max ${MAX_PATHS}).`;
		} else if (parsed.paths.length > 0) {
			paths = parsed.paths;
		}

		if (Object.keys(errors).length > 0) return { success: false, errors };
		return {
			success: true,
			data: {
				domain: domain as string,
				teamId: teamId as string,
				bundleId: bundleId as string,
				label,
				paths,
			},
		};
	},
};

// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------

export function statusBadgeClass(status: IosUniversalLinkStatus): string {
	switch (status) {
		case "active":
			return "bg-emerald-50 text-emerald-700 border-emerald-200";
		case "paused":
			return "bg-amber-50 text-amber-700 border-amber-200";
		case "archived":
			return "bg-zinc-100 text-zinc-600 border-zinc-200";
	}
}

export function formatTimestamp(seconds: number | null): string {
	if (seconds === null || Number.isNaN(seconds)) return "-";
	const d = new Date(seconds * 1000);
	if (Number.isNaN(d.getTime())) return "-";
	return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, "Z");
}

export function appIdentifier(row: IosUniversalLinkAppView): string {
	return `${row.team_id}.${row.bundle_id}`;
}

export function sortApps(
	apps: readonly IosUniversalLinkAppView[],
): IosUniversalLinkAppView[] {
	const rank: Record<IosUniversalLinkStatus, number> = {
		active: 0,
		paused: 1,
		archived: 2,
	};
	return apps
		.slice()
		.sort((a, b) => {
			const r = rank[a.status] - rank[b.status];
			if (r !== 0) return r;
			if (a.domain !== b.domain) return a.domain.localeCompare(b.domain);
			return b.created_at - a.created_at;
		});
}

export function groupByDomain(
	apps: readonly IosUniversalLinkAppView[],
): Array<{ domain: string; apps: IosUniversalLinkAppView[] }> {
	const bucket = new Map<string, IosUniversalLinkAppView[]>();
	for (const a of apps) {
		const existing = bucket.get(a.domain);
		if (existing) {
			existing.push(a);
		} else {
			bucket.set(a.domain, [a]);
		}
	}
	const out: Array<{ domain: string; apps: IosUniversalLinkAppView[] }> = [];
	const domains = Array.from(bucket.keys()).sort((a, b) => a.localeCompare(b));
	for (const d of domains) {
		const rows = bucket.get(d) ?? [];
		out.push({ domain: d, apps: sortApps(rows) });
	}
	return out;
}

export function countByStatus(
	apps: readonly IosUniversalLinkAppView[],
): Record<IosUniversalLinkStatus, number> {
	const out: Record<IosUniversalLinkStatus, number> = {
		active: 0,
		paused: 0,
		archived: 0,
	};
	for (const a of apps) out[a.status] += 1;
	return out;
}

export function aasaUrl(domain: string): string {
	return `https://${domain}/.well-known/apple-app-site-association`;
}

export function buildAASAFromRows(
	apps: readonly IosUniversalLinkAppView[],
): AASADocument {
	const active = apps.filter((a) => a.status === "active");
	return {
		applinks: {
			apps: [],
			details: active.map((a) => ({
				appIDs: [appIdentifier(a)],
				components: a.paths.map((p) => ({ "/": p })),
			})),
		},
	};
}

export function renderAASAPreview(
	apps: readonly IosUniversalLinkAppView[],
): string {
	return JSON.stringify(buildAASAFromRows(apps), null, 2);
}

// ---------------------------------------------------------------------------
// Remote operations
// ---------------------------------------------------------------------------

const BASE = "/ios-universal-links";

interface CreateResponse {
	app: IosUniversalLinkAppView;
}
interface ListResponse {
	apps: IosUniversalLinkAppView[];
}
interface GetResponse {
	app: IosUniversalLinkAppView;
}
interface PatchResponse {
	app: IosUniversalLinkAppView;
}
interface PreviewResponse {
	domain: string;
	aasa: AASADocument;
	app_count: number;
}
export type DeleteResponse =
	| { deleted: true; id: string }
	| { archived: true; app: IosUniversalLinkAppView };

export async function createApp(
	payload: ValidatedAppDraft,
): Promise<IosUniversalLinkAppView> {
	const res = await apiClient.post<CreateResponse>(BASE, {
		domain: payload.domain,
		team_id: payload.teamId,
		bundle_id: payload.bundleId,
		label: payload.label,
		paths: payload.paths,
	});
	return res.app;
}

export async function listApps(
	domain?: string,
): Promise<IosUniversalLinkAppView[]> {
	const query = domain?.trim() ? { domain: domain.trim() } : undefined;
	try {
		const res = await apiClient.get<ListResponse>(BASE, { query });
		return res.apps ?? [];
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return [];
		throw err;
	}
}

export async function getApp(
	id: string,
): Promise<IosUniversalLinkAppView | null> {
	if (!id.trim()) return null;
	try {
		const res = await apiClient.get<GetResponse>(
			`${BASE}/${encodeURIComponent(id)}`,
		);
		return res.app;
	} catch (err) {
		if (err instanceof ApiError && err.status === 404) return null;
		throw err;
	}
}

export interface PatchPayload {
	label?: string;
	paths?: string[];
	status?: IosUniversalLinkStatus;
}

export async function patchApp(
	id: string,
	patch: PatchPayload,
): Promise<IosUniversalLinkAppView> {
	const body: Record<string, unknown> = {};
	if (patch.label !== undefined) body.label = patch.label;
	if (patch.paths !== undefined) body.paths = patch.paths;
	if (patch.status !== undefined) body.status = patch.status;
	const res = await apiClient.patch<PatchResponse>(
		`${BASE}/${encodeURIComponent(id)}`,
		body,
	);
	return res.app;
}

export async function deleteApp(id: string): Promise<DeleteResponse> {
	const res = await apiClient.delete<DeleteResponse>(
		`${BASE}/${encodeURIComponent(id)}`,
	);
	return res;
}

export async function previewAASA(domain: string): Promise<PreviewResponse> {
	const res = await apiClient.get<PreviewResponse>(`${BASE}/preview`, {
		query: { domain },
	});
	return res;
}

// ---------------------------------------------------------------------------
// Cache mutators
// ---------------------------------------------------------------------------

export function replaceInList(
	list: readonly IosUniversalLinkAppView[],
	next: IosUniversalLinkAppView,
): IosUniversalLinkAppView[] {
	let hit = false;
	const out = list.map((row) => {
		if (row.id === next.id) {
			hit = true;
			return next;
		}
		return row;
	});
	if (!hit) out.push(next);
	return out;
}

export function removeFromList(
	list: readonly IosUniversalLinkAppView[],
	id: string,
): IosUniversalLinkAppView[] {
	return list.filter((row) => row.id !== id);
}

export const iosUniversalLinksQueryKeys = {
	all: ["ios-universal-links"] as const,
	list: (domain?: string) =>
		[...iosUniversalLinksQueryKeys.all, "list", domain ?? "__all__"] as const,
	single: (id: string) =>
		[...iosUniversalLinksQueryKeys.all, "single", id] as const,
	preview: (domain: string) =>
		[...iosUniversalLinksQueryKeys.all, "preview", domain] as const,
};
